import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { useCookieConsent } from "../components/CookieConsent";

const LI_CONVERSION_ID = 30072754;
const LI_FIRED_KEY = "vemoye_li_conversion_fired_v1";

const readFiredSet = () => {
  try {
    const raw = sessionStorage.getItem(LI_FIRED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
};
const markFired = (id) => {
  try {
    const set = readFiredSet();
    set.add(id);
    sessionStorage.setItem(LI_FIRED_KEY, JSON.stringify(Array.from(set)));
  } catch {}
};

/**
 * Dedicated URL for waitlisted applicants (per Vemoye brief).
 * Reached via navigate() from Join.jsx after the backend confirms a
 * successful application submission. Fires the LinkedIn conversion event
 * (conversion_id 30072754) exactly once per successful application, only
 * when marketing consent has been granted.
 */
const JoinWaitlisted = () => {
  const location = useLocation();
  const firstName = location.state?.firstName || null;
  const applicationId = location.state?.applicationId || null;
  const justSubmitted = !!location.state?.justSubmitted;
  const { marketing } = useCookieConsent();
  const attemptedRef = useRef(false);

  useEffect(() => {
    // Strict guards: only fire once, only after a real successful
    // submission, only with an application ID, only with consent, only
    // when the Insight Tag has actually loaded.
    if (attemptedRef.current) return;
    if (!justSubmitted || !applicationId) return;
    if (!marketing) return;
    if (typeof window.lintrk !== "function") return;
    const fired = readFiredSet();
    if (fired.has(applicationId)) return;

    attemptedRef.current = true;
    try {
      window.lintrk("track", { conversion_id: LI_CONVERSION_ID });
      markFired(applicationId);
    } catch {
      // Never let ad-tag failures affect the confirmation UX.
    }
  }, [justSubmitted, applicationId, marketing]);

  return (
    <section
      className="max-w-[1000px] mx-auto px-6 lg:px-10 py-24 md:py-32 text-center"
      data-testid="join-waitlisted-page"
    >
      <CheckCircle2 size={60} className="mx-auto mb-8" style={{ color: "var(--gold)" }} />

      <div className="section-eyebrow mb-4" style={{ color: "rgba(18,52,40,0.6)" }}>
        Diaspora Network · Application received
      </div>

      <h1 className="hero-title text-[46px] md:text-[80px] leading-[0.98] mb-6">
        You&rsquo;re{" "}
        <span className="font-serif-i" style={{ color: "var(--orange)" }}>on the list.</span>
      </h1>

      <p className="max-w-[680px] mx-auto text-[16px] md:text-[17px] leading-relaxed mb-4" style={{ color: "rgba(18,52,40,0.85)" }}>
        {firstName ? <>Thank you, <strong style={{ color: "var(--forest)" }}>{firstName}</strong>. </> : "Thank you. "}
        We&rsquo;re currently building the founding cohort of the Vemoye Diaspora Network. Your application
        has been received and added to the waiting list ahead of the membership platform launch.
      </p>

      <p className="max-w-[680px] mx-auto text-[15px] leading-relaxed" style={{ color: "rgba(18,52,40,0.7)" }}>
        We&rsquo;ll be in touch when applications begin review. In the meantime, keep an eye on your inbox
        &mdash; and feel free to introduce Vemoye to another Nigerian diaspora professional who should be
        part of this.
      </p>

      <div className="mt-12 grid md:grid-cols-3 gap-4 max-w-[720px] mx-auto text-left">
        {[
          { n: "01", t: "Watch your inbox", d: "Confirmation email is on its way. Add hello@vemoyefoundation.org to your contacts." },
          { n: "02", t: "Review begins soon", d: "Applications will be reviewed ahead of the founding cohort launch." },
          { n: "03", t: "Share the network", d: "Tell one Nigerian diaspora professional who should join too." },
        ].map((s) => (
          <div key={s.n} className="pt-4 border-t" style={{ borderColor: "rgba(18,52,40,0.12)" }}>
            <div className="font-serif-i text-[22px] mb-1" style={{ color: "var(--gold)" }}>{s.n}</div>
            <div className="text-[15px] font-medium mb-1" style={{ color: "var(--forest)" }}>{s.t}</div>
            <div className="text-[13px] leading-relaxed" style={{ color: "rgba(18,52,40,0.7)" }}>{s.d}</div>
          </div>
        ))}
      </div>

      <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/"
          data-testid="waitlisted-home-cta"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md btn-gold text-[14px] font-medium"
        >
          Back to the Network <ArrowUpRight size={16} />
        </Link>
        <a
          href="https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Fwww.vemoyefoundation.org%2F"
          target="_blank"
          rel="noreferrer"
          data-testid="waitlisted-share-cta"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md btn-outline text-[14px] font-medium"
        >
          Share on LinkedIn
        </a>
      </div>
    </section>
  );
};

export default JoinWaitlisted;
